/* Whether the next arrival at a scroll-restoring page is a RETURN or is NEW WORK.
 *
 * The Workspace restores your scroll offset, and that is right when you are coming back to a
 * long page you were reading through. It is wrong when you got there by generating: you just
 * made something, the new card is prepended to the top-left of the first row, and restoring
 * where you happened to be reading last time drops you below it. The page then looks like the
 * submission did nothing — the same failure the row-rewind on that page already guards
 * against horizontally.
 *
 * Home already encodes this distinction; its own comment says arriving there "almost always
 * means starting something new — so it still resets, deliberately". Generating into the
 * Workspace is that same case, and this is how the arrival says so.
 *
 * Read once and cleared, so it can only ever affect the navigation it was set for — a flag
 * set by a navigation that then does not happen cannot silently change a later one.
 */
let atTop = false;

/** Mark the next navigation as new work: land at the top, do not restore. */
export const arriveAtTop = () => { atTop = true; };

/** Consume the mark. True only for the one arrival it was set for. */
export const takeArriveAtTop = () => {
  const v = atTop;
  atTop = false;
  return v;
};

/* Hold the page at the top across the arrival, keyed to what actually moves it.
 *
 * MEASURED, on a real device, arriving at a video page:
 *
 *   +  0ms y= 0 vt=-127 vp=0  vh=788 h=788   ←start
 *   + 86ms y=34 vt=0    vp=34 vh=676 h=788   ←vv-resize
 *   +572ms y=34 vt=0    vp=34 vh=676 h=1773  ←grew
 *
 * At 86ms the VISUAL VIEWPORT SHRINKS, 788 to 676 — Safari's toolbars expanding — and in that
 * same event the document scrolls from 0 to 34. The browser is compensating for its own chrome
 * appearing. Nothing moves it back, and the rest of the trace is just the page growing to its
 * full height with the offset still on it.
 *
 * That is why every earlier fix missed. The reset runs at t=0 and is long finished by 86ms;
 * scrollRestoration has nothing to do with it; and a fixed-duration poll only worked by
 * accident, when its window happened to still be open. The trigger is an event, so listen for
 * the event.
 *
 * The window is generous because the chrome can settle more than once — the workspace trace
 * showed four separate shifts across half a second — and cheap, because between events this
 * costs nothing at all.
 *
 * IT MUST NOT FIGHT THE USER: a touch, wheel or key hands control back immediately, and it
 * only ever acts when the page is not already at the top.
 *
 * Returns its own release function so an effect can cancel it on unmount.
 */
const holdAt = (targetY, ms) => {
  const vv = window.visualViewport;
  let ro = null;
  const deadline = performance.now() + ms;
  let released = false;
  let raf = 0;

  const release = () => {
    if (released) return;
    released = true;
    cancelAnimationFrame(raf);
    window.removeEventListener("touchstart", release);
    window.removeEventListener("wheel", release);
    window.removeEventListener("keydown", release);
    if (vv) {
      vv.removeEventListener("resize", reassert);
      vv.removeEventListener("scroll", reassert);
    }
    if (ro) { ro.disconnect(); ro = null; }
  };

  /* Re-assert for a few frames after the event, not just inside it.
   *
   * Tapping a video was fixed by answering the event synchronously; generating was not, and
   * the difference is the keyboard. Submitting blurs the composer, so the viewport change is
   * the keyboard leaving as well as the toolbars arriving — a bigger move, animated over
   * several frames, and iOS applies its scroll compensation AFTER the event has fired. A reset
   * written inside the handler is simply overwritten later in the same frame. */
  /* A REAL DELTA, NOT A REPEAT OF THE VALUE ALREADY STORED.
   *
   * The generate trace ends at y=0 and stays there while the page still renders scrolled by
   * 65px — iOS reports the scroll position it was told and goes on RENDERING the old one. So
   * `if (scrollY !== 0) scrollTo(0, 0)` skips: the number is already 0, and writing 0 over 0
   * is a no-op that repaints nothing. Stepping to 1 and back to 0 is a genuine change the
   * compositor has to honour. Same class of fault as the stranded nav bar, and the same shape
   * of fix — make the engine redo the work rather than ask it to.
   *
   * Once per viewport event rather than every frame: two writes per event is invisible,
   * whereas doing it continuously would be a 1px jitter. */
  const force = () => {
    /* THE LAYOUT READ BETWEEN THE WRITES IS THE WHOLE FIX.
     *
     * 1.0.43 did `scrollTo(0, 1)` then `scrollTo(0, 0)` and changed nothing, because two
     * writes in the same task are COALESCED: the engine only ever commits the final value,
     * sees 0 where it already had 0, and repaints nothing. Reading a layout property forces
     * the intermediate 1 to be committed, so the following 0 is a real change with a real
     * delta and iOS has to render it.
     *
     * Confirmed by the user: on the broken page you CAN scroll up and find content, so the
     * document genuinely is offset while scrollY reports 0. The engine holds the right number
     * and is not drawing it — the same fault as the stranded nav bar in 1.0.34, which was
     * likewise fixed by forcing the work to be redone rather than by asking again. */
    window.scrollTo(0, targetY);
    /* AND A RELATIVE NUDGE. This is the published workaround for WebKit #297779 (see also
       Apple Developer Forums 800125), where after a keyboard dismissal on iOS 26 the visual
       viewport does not restore and everything renders offset. Both workarounds in the wild
       use scrollBy rather than scrollTo, and the reason is the one that has defeated every
       attempt here: an ABSOLUTE write of the value the engine already holds is a no-op it is
       free to skip, while a RELATIVE delta is a real movement it has to process — which is
       what forces the viewport recalculation. Net zero, so nothing visibly moves. */
    window.scrollBy(0, 1);
    window.scrollBy(0, -1);
  };

  /* ONLY ACT WHEN THERE IS SOMETHING TO FIX.
   *
   * This used to call forceTop unconditionally on every event, plus a follow-up frame. With a
   * ResizeObserver attached that meant three scroll writes per content growth, and a grid of
   * thumbnails produces a great many growths — which is what made the arrival choppy once the
   * real bug was fixed.
   *
   * The unconditional version existed because scrollY was lying: it reported 0 while the page
   * rendered 67 down, so a guard would have skipped every time. That lie was the inherited
   * keyboard offset, and the page no longer carries one, so the guard is trustworthy again
   * and this is silent in the normal case. */
  function reassert() {
    if (released) return;
    if (performance.now() > deadline) return release();
    // A tolerance rather than equality: the target can be a mid-page offset now, and a
    // sub-pixel difference is not the 34px shift this exists to answer.
    if (Math.abs(window.scrollY - targetY) > 1) force();
  }

  // touchstart rather than scroll: on iOS it arrives BEFORE the scroll it causes, so control
  // is handed over without a frame of fighting. Listening for `scroll` would be useless here —
  // this function causes scroll events itself.
  window.addEventListener("touchstart", release, { passive: true });
  window.addEventListener("wheel", release, { passive: true });
  window.addEventListener("keydown", release);

  if (vv) {
    vv.addEventListener("resize", reassert);
    vv.addEventListener("scroll", reassert);
  }

  /* CONTENT GROWTH IS A TRIGGER TOO, and it was the one being missed.
   *
   * Measured: the hold corrects the shift at 74ms, 453ms and 486ms, goes quiet, and then at
   * 2270ms the page GROWS as the rest of the videos load — and at 2324ms iOS shifts it to 67
   * again and it stays, because by then the 2s window had closed. A growth fires no viewport
   * event whatsoever, so listening only to visualViewport could never have caught the one
   * that actually stuck.
   *
   * Hence both changes: watch the document's size, and stay armed long enough to cover a
   * grid of thumbnails finishing. 8s sounds long and costs nothing — between events this does
   * no work, and the first touch ends it. */
  ro = new ResizeObserver(reassert);
  ro.observe(document.documentElement);

  // A short frame-by-frame burst as well, for the first moments: the chrome can settle before
  // any listener is attached, and an early shift would otherwise sit there unanswered until
  // the next event that may never come.
  const burstUntil = performance.now() + 250;
  const tick = () => {
    if (released) return;
    if (performance.now() > burstUntil) return force(); // one real nudge to finish on
    if (Math.abs(window.scrollY - targetY) > 1) window.scrollTo(0, targetY);
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return release;
};

/** Hold the page at the top across an arrival. Unchanged behaviour; now a thin wrapper. */
export const holdAtTop = (ms = 1500) => holdAt(0, ms);

/* Hold the page WHERE IT IS across an interaction that is not a navigation.
 *
 * Same fault, different trigger. Everything above is armed by RouteTransitionManager on a
 * route change, and the chrome expansion it answers is not caused by routing — it is caused by
 * the tap. Pressing "Sign in with Google" on the landing page changes no route: the page stays
 * put and then leaves for accounts.google.com, so nothing was ever armed and the 34px
 * compensation lands unanswered. That is the "screen teleports downwards before it changes
 * screens" reported on 2026-09-22.
 *
 * AT THE CURRENT OFFSET, not the top. The button sits below the fold on a phone, so the
 * visitor has scrolled to reach it; snapping them to the top would replace one unexplained
 * jump with another. */
export const holdPosition = (ms = 1500) =>
  holdAt(typeof window === "undefined" ? 0 : window.scrollY, ms);
